import localforage from 'localforage';
import {
  getLegacyStorageItem,
  removeLegacyStorageItem,
  setLegacyStorageItem,
} from '@/shared/storageLocalLegacy';

export const APP_TABLES_IDB_KEYS = [
  'selected_client',
  'user_logado',
  'rca_usuario',
  'order_numbers',
  'new_data_sets',
  'datasets',
  'cart_id',
  'cart_details',
  'paymentCode',
  'user_login_data',
  'discounts',
] as const;

export type AppTableIdbKey = (typeof APP_TABLES_IDB_KEYS)[number];

const appTablesStore = localforage.createInstance({
  name: 'PWA_RCA',
  storeName: 'app_tables',
  driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
});

const cache = new Map<string, any>();
let hydrated = false;

export function isAppTableIdbKey(name: string): name is AppTableIdbKey {
  return (APP_TABLES_IDB_KEYS as readonly string[]).includes(name);
}

export async function readAllAppTablesForBackup(): Promise<Partial<Record<AppTableIdbKey, unknown>>> {
  const out: Partial<Record<AppTableIdbKey, unknown>> = {};
  for (const name of APP_TABLES_IDB_KEYS) {
    try {
      let v = await appTablesStore.getItem(name);
      if (v === null || v === undefined) {
        v = getLegacyStorageItem(name);
      }
      if (v !== null && v !== undefined) {
        out[name] = v;
      }
    } catch {
      //
    }
  }
  return out;
}

export function restoreAppTablesToLegacy(backup: Partial<Record<AppTableIdbKey, unknown>>) {
  for (const name of APP_TABLES_IDB_KEYS) {
    const v = backup[name];
    if (v !== null && v !== undefined) {
      setLegacyStorageItem(name, v);
    }
  }
}

export async function hydrateAppTablesFromIdb(): Promise<void> {
  if (hydrated) return;
  for (const name of APP_TABLES_IDB_KEYS) {
    let v = await appTablesStore.getItem(name);
    if (v !== null && v !== undefined) {
      cache.set(name, v);
      removeLegacyStorageItem(name);
      continue;
    }
    const leg = getLegacyStorageItem(name);
    cache.set(name, leg);
    if (leg !== null && leg !== undefined) {
      await appTablesStore.setItem(name, leg);
      removeLegacyStorageItem(name);
    }
  }
  hydrated = true;
}

export function resetAppTablesIdbCacheForTests() {
  cache.clear();
  hydrated = false;
}

export function appTableGet(name: AppTableIdbKey): any | null {
  if (hydrated && cache.has(name)) {
    return cache.get(name) as any;
  }
  return getLegacyStorageItem(name);
}

export function appTableSet(name: AppTableIdbKey, data: any): boolean {
  cache.set(name, data);
  removeLegacyStorageItem(name);
  void appTablesStore.setItem(name, data).catch((e) => console.error(e));
  return true;
}

export function appTableRemove(name: AppTableIdbKey): void {
  cache.set(name, null);
  removeLegacyStorageItem(name);
  void appTablesStore.removeItem(name).catch((e) => console.error(e));
}
